import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import { drawPage, openPdf } from '../../../lib/pdfRenderer';

const HIGHLIGHT = 'rgba(255, 193, 7, 0.45)';
const PAGE_GAP = 12;

/** 선택 라인의 좌표(source_ref.box, % 단위)를 페이지 위에 덧그린다. */
function LineBoxes({ lines, page, activeRef, onPickLine }) {
    return lines
        .filter((l) => l.source_ref?.box && (l.source_ref.page ?? 1) === page)
        .map((l) => {
            const { x, y, w, h } = l.source_ref.box;
            const active = activeRef?.lineId === l.id;
            return <Box key={l.id} onClick={() => onPickLine(l.id)} sx={{ position: 'absolute', left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%`, bgcolor: active ? HIGHLIGHT : 'rgba(0,100,126,0.08)', border: active ? '2px solid #ffb300' : '1px dashed rgba(0,100,126,0.4)', cursor: 'pointer' }} />;
        });
}

/** 화면 위아래 한 화면 안에 든 페이지까지 미리 그린다. */
const NEAR_MARGIN = '100% 0px';

/** 페이지 하나. 자리는 처음부터 잡고 비트맵은 화면 가까이 올 때만 그린다 — 긴 PDF도 탭이 버틴다. 멀어지면 비우고, 폭이 바뀌면 다시 그린다. */
function PdfPage({ pdf, number, width, lines, activeRef, onPickLine, active, scrollRef, rootRef }) {
    const boxRef = useRef(null);
    const canvasRef = useRef(null);
    const [ratio, setRatio] = useState(null);
    const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');
    useEffect(() => {
        let alive = true;
        pdf.getPage(number).then((page) => {
            if (!alive) return;
            const base = page.getViewport({ scale: 1 });
            setRatio(base.height / base.width);
        }).catch(() => {});
        return () => { alive = false; };
    }, [pdf, number]);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined' || !boxRef.current) return undefined;
        const observer = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), { root: rootRef.current, rootMargin: NEAR_MARGIN });
        observer.observe(boxRef.current);
        return () => observer.disconnect();
    }, [rootRef]);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        if (!near) {
            canvas.width = 0;
            canvas.height = 0;
            return undefined;
        }
        let cancel = () => {};
        let alive = true;
        pdf.getPage(number).then((page) => {
            if (alive) cancel = drawPage(page, canvas, width);
        }).catch(() => {});
        return () => { alive = false; cancel(); };
    }, [pdf, number, width, near]);
    const setRefs = (el) => {
        boxRef.current = el;
        if (active) scrollRef.current = el;
    };
    return (
        <Box ref={setRefs} sx={{ position: 'relative', width, minHeight: ratio ? width * ratio : width * 1.3, mx: 'auto', mb: `${PAGE_GAP}px`, bgcolor: 'white', boxShadow: 1 }}>
            <canvas ref={canvasRef} role="img" aria-label={`Page ${number}`} style={{ display: 'block' }} />
            <Box sx={{ position: 'absolute', inset: 0 }}><LineBoxes lines={lines} page={number} activeRef={activeRef} onPickLine={onPickLine} /></Box>
        </Box>
    );
}

/** pdf.js를 쓰지 못할 때(차단·CORS·구형 브라우저) 내장 뷰어로 보여준다. 사유를 배지에 남겨 문의 때 바로 안다. */
function PdfFrame({ fileUrl, page, zoom, reason }) {
    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <iframe title="pl-pdf-frame" src={`${fileUrl}#page=${page}&zoom=${zoom}&toolbar=0`} style={{ width: '100%', height: '100%', border: 'none' }} />
            <Tooltip title={`The built-in PDF viewer could not be used: ${reason}`}>
                <Chip size="small" label="Browser viewer" sx={{ position: 'absolute', top: 8, right: 24, fontSize: 10, height: 18, bgcolor: 'white' }} />
            </Tooltip>
        </Box>
    );
}

/**
 * 담당자가 원본을 보는 PDF 뷰어. 페이지를 세로로 잇고 100%를 패널 폭에 맞춘다. 확대는 같은 자리에서 다시 그려 깜박이지 않고 스크롤이 유지된다.
 * 선택 라인의 페이지로 스크롤한다. 뿌리 요소의 data-*는 테스트가 무엇을 열었는지 보는 용도다.
 */
export default function PdfCanvasViewer({ fileUrl, lines, activeRef, onPickLine, zoom = 100 }) {
    const page = activeRef?.page ?? 1;
    const containerRef = useRef(null);
    const activePageRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [state, setState] = useState({ status: 'loading', pdf: null, reason: null });

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => setContainerWidth(el.clientWidth);
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setState({ status: 'loading', pdf: null, reason: null });
        openPdf(fileUrl, { signal: controller.signal })
            .then((pdf) => { if (!controller.signal.aborted) setState({ status: 'ready', pdf, reason: null }); })
            .catch((error) => {
                if (controller.signal.aborted) return;
                const reason = error?.message ?? String(error);
                if (process.env.NODE_ENV !== 'test') console.warn('PDF viewer fell back to the browser viewer:', reason); // eslint-disable-line no-console
                setState({ status: 'fallback', pdf: null, reason });
            });
        return () => controller.abort(); // 열린 문서·워커도 함께 닫힌다(openPdf)
    }, [fileUrl]);

    useEffect(() => {
        activePageRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }, [page, state.status]);

    const pageWidth = Math.max(100, Math.floor((containerWidth - 32) * (zoom / 100)));
    return (
        <Box ref={containerRef} title="pl-pdf" data-src={fileUrl} data-page={page} data-zoom={zoom} sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2, boxSizing: 'border-box' }}>
            {state.status === 'loading' && <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}
            {state.status === 'fallback' && <PdfFrame fileUrl={fileUrl} page={page} zoom={zoom} reason={state.reason} />}
            {state.status === 'ready' && containerWidth > 0 && Array.from({ length: state.pdf.numPages }, (_, i) => i + 1).map((n) => (
                <PdfPage key={n} pdf={state.pdf} number={n} width={pageWidth} lines={lines} activeRef={activeRef} onPickLine={onPickLine} active={n === page} scrollRef={activePageRef} rootRef={containerRef} />
            ))}
        </Box>
    );
}
